import { lazy } from 'react';
import { createRoute } from '@tanstack/react-router';
import * as z from 'zod';

import { createTenantApp } from '@/app/create-tenant-app';
import { tokenAuthGuard } from '@/app/routing/token-auth-guard';
import type { RouterContext } from '@/app/routing/router-context';
import { homeSearchSchema } from './home/home';
import { morningstarHomeLoader } from './home/home.loader';
import { collectionOptions } from './collection/collection.hooks';
// `pendingComponent`s for the two custom routes below -- eagerly imported
// (not lazy) so they're available immediately as the Suspense fallback
// while `Collection`/`Issue`'s own chunks load; both are cheap (just
// `@shared/ui/topbar` + `@shared/ui/loading`). Without these, that window
// fell back to `createTenantApp`'s generic `RoutePending` (no real navbar)
// -- same bug class as improvements/07.1-per-template-issue-reader-widgets
// .md's Phase 0.2b/0.7, here for the two morningstar-exclusive routes those
// phases didn't cover.
import { CollectionRouteLoading } from './collection/route-loading';
import { Loading as IssueRouteLoading } from '@pages/issue/ui/loading';

// `Home` stays a static import, same as daily/toyota: `validateSearch`
// below needs `homeSearchSchema` from this same module at router-definition
// time, so the module is already in the eager graph regardless -- wrapping
// the component in `lazy()` too would add a Suspense boundary for no actual
// byte savings (same reasoning applies wherever a template's home schema
// lives in the home module itself).
import Home from './home/home';

// `Collection` and `Issue` (both morningstar-exclusive, no schema coupling)
// go back to `React.lazy()`, matching every other template's custom routes
// (see `app/routing/lazy/README.md` for why catalog routes use
// `route.lazy()` instead -- these two are template-exclusive, not catalog
// routes). Static imports were a stopgap in 2abe7f9 (2026-07-25) for a
// "routes fail to load once built" symptom that was never root-caused. All
// nine other templates lazy-load their equivalent custom routes
// successfully in production today, so the eager-import version was
// working around a bug elsewhere (base path / SW precache), not a defect
// in `React.lazy()` itself -- see improvements/10-performance-bundle-size.md.
const Collection = lazy(() => import('./collection/collection'));
const Issue = lazy(() => import('@pages/issue'));

// `morningstar`'s own composition of the shared issue-reader widgets
// (improvements/07.1-per-template-issue-reader-widgets.md, Phase 2) --
// substituted into the catalog's `issue`/`issueArticle` routes below via
// `protect.*.provide`'s `IssueComponent`, per `app/routing/lazy/issue.lazy
// .tsx`'s context-override mechanism. Lazy for the same reason `Issue`
// above is: this is the identity/loader-sharing catalog route, not a
// template-exclusive one, so nothing about substituting the component
// should make it any less code-split than the default every other template
// still gets.
const MorningstarIssueView = lazy(() => import('./issue/issue'));

// `AuthCollection` below reads `token` via `useAuth`'s `useSearch({ strict:
// false })`; declared here so it's actually validated per this route
// instead of relying on unknown-param passthrough.
export const collectionSearchSchema = z.object({
	token: z.string().optional(),
});

export const router = createTenantApp({
	home: {
		path: '/',
		validateSearch: homeSearchSchema,
		component: Home,
		loader: morningstarHomeLoader,
		// `redirectIfUnauthenticated: false` -- home has to stay reachable
		// with no session at all, since it's what shows the token/login
		// entry point in the first place. Still runs the token-exchange/
		// URL-cleanup half of the guard when a reader arrives via `?token=`.
		beforeLoad: (opts) =>
			tokenAuthGuard(
				{
					params: opts.params,
					search: opts.search as Record<string, unknown>,
					pathname: opts.location.pathname,
				},
				{ redirectIfUnauthenticated: false },
			),
	},
	routes: ['issue', 'issueArticle', 'drafts', 'favorite', 'previewView'],
	// `issue`/`issueArticle` keep the catalog's default guard exactly
	// (`route-catalog.tsx`'s `DEFAULT_ISSUE_PROTECTION` -- morningstar has no
	// auth wiring of its own, so both routes fell back to that default
	// before this change too); the only addition is `provide`, which
	// substitutes morningstar's own issue page for the catalog's default
	// `@pages/issue` `Issue` via `issue.lazy.tsx`'s `IssueComponent` context
	// read. Declared identically for both routes so navigating between
	// `/issues/$id` and `/issues/$id/$articleId` never swaps components
	// mid-session -- both render the same `MorningstarIssueView`.
	protect: {
		issue: {
			beforeLoad: (context) =>
				tokenAuthGuard(context, { redirectIfUnauthenticated: true }),
			provide: { IssueComponent: MorningstarIssueView },
		},
		issueArticle: {
			beforeLoad: (context) =>
				tokenAuthGuard(context, { redirectIfUnauthenticated: true }),
			provide: { IssueComponent: MorningstarIssueView },
		},
	},
	// `issues` (plain Issue component, no $id, no auth) and `collections/$id`
	// are both morningstar-exclusive -- no other template has either.
	custom: (rootRoute) => [
		createRoute({
			getParentRoute: () => rootRoute,
			component: Issue,
			path: 'issues',
			pendingComponent: IssueRouteLoading,
		}),
		createRoute({
			getParentRoute: () => rootRoute,
			component: Collection,
			validateSearch: collectionSearchSchema,
			path: 'collections/$id',
			pendingComponent: CollectionRouteLoading,
			beforeLoad: (opts) =>
				tokenAuthGuard(
					{
						params: opts.params,
						search: opts.search as Record<string, unknown>,
						pathname: opts.location.pathname,
					},
					{ redirectIfUnauthenticated: true },
				),
			// Same reasoning as `route-catalog.tsx`'s `previewViewRoute`
			// loader (user-directed 2026-08-28: prefetch collections on
			// hover the same way issues already are). Without this,
			// `createTenantApp`'s `defaultPreload: 'intent'` only ever
			// preloaded this route's lazy *chunk* on hover -- the actual
			// collection data fetch only started once `View()`
			// (`collection.tsx`) mounted, i.e. after the click, not the
			// hover. `collectionOptions` is the exact factory
			// `useGetCollection` itself calls, keyed on the same
			// `queryKeys.collectionIssue(id)`, so a genuine hover-then-click
			// reuses this already-in-flight/settled request instead of
			// starting a second one.
			//
			// Wrapped in try/catch and swallowed, not left to reject into a
			// route-level error boundary: `collectionOptions`' queryFn has no
			// offline-specific handling of its own (see that factory's doc
			// comment -- `useGetCollection` never had any either), so a
			// failed prefetch -- offline, or a genuine network error -- just
			// falls through to `View()`'s own `useGetCollection` call
			// re-attempting the same query on mount and rendering its
			// existing inline error state, exactly as it did before this
			// loader existed. A route-level crash here would be a strictly
			// worse experience than that already-graceful fallback.
			loader: async ({ context, params }: CollectionLoaderArgs) => {
				try {
					await context.queryClient.ensureQueryData(
						collectionOptions(params.id, {
							isAdmin: context.auth.isAdmin(),
							ttlMinutes: context.config.app?.cache?.ttl?.getIssue,
						}),
					);
				} catch {
					// See the loader's own comment above -- intentional.
				}
			},
		}),
	],
});

interface CollectionLoaderArgs {
	context: RouterContext;
	params: { id: string };
}
